// Admin panel API client
const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'));

export const API_BASE: string =
  (import.meta.env.VITE_API_BASE as string) || (isLocal ? 'http://localhost:3004' : 'https://api.inboxme.space');

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function apiFetch(path: string, init: RequestInit = {}, opts: { noRedirectOn401?: boolean; retries?: number } = {}): Promise<Response> {
  const maxRetries = opts.retries ?? 2;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders(),
        ...(init.headers || {}),
      },
    });
    if (res.status === 401 && !opts.noRedirectOn401) {
      localStorage.removeItem('admin_token');
      // Only hard-redirect if we're NOT already on /login — otherwise
      // unauthenticated polling causes an infinite reload loop.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login';
      }
      return res;
    }
    if (res.status === 429 && attempt < maxRetries) {
      await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      continue;
    }
    return res;
  }
  // unreachable but TypeScript needs it
  return fetch(`${API_BASE}${path}`, init);
}

export async function login(email: string, password: string): Promise<string> {
  const res = await fetch(`${API_BASE}/api/admin/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) throw new Error('Invalid credentials');
  const data = await res.json();
  return data.token as string;
}

export interface ContentSection {
  heading: string;
  body: string;
  image_url: string;
}

export interface Faq {
  q: string;
  a: string;
}

export interface InternalLink {
  slug: string;
  label: string;
}

export interface Post {
  id: string;
  title: string;
  slug: string;
  body: string;
  lang: 'en' | 'tr' | 'de' | 'fr' | 'id' | 'pl' | 'pt' | 'ar' | 'hi' | 'ur';
  status: 'draft' | 'scheduled' | 'published' | 'disabled';
  publish_at?: number;
  meta_description?: string;
  author?: string;
  cover_image_url?: string;
  mobile_cover_image_url?: string;
  tags?: string[];
  word_count?: number;
  read_time_min?: number;
  meta?: Record<string, unknown>;
  // Structured content
  sections?: ContentSection[];
  faqs?: Faq[];
  internal_links?: InternalLink[];
  cta_text?: string;
  cta_button_label?: string;
  cta_button_url?: string;
  meta_og_image?: string;
  meta_keywords?: string;
  post_type?: 'blog' | 'guide';
  /** 1 = served from D1 at request time (admin panel), 0 = prerendered. */
  is_dynamic?: number;
  /** When this slug was last submitted to IndexNow (Bing/Yandex). */
  indexnotified_at?: number | null;
  created_at: number;
  updated_at: number;
}

export async function fetchPosts(params: { status?: string; lang?: string } = {}): Promise<Post[]> {
  const qs = new URLSearchParams();
  if (params.status && params.status !== 'all') qs.set('status', params.status);
  if (params.lang && params.lang !== 'all') qs.set('lang', params.lang);
  const q = qs.toString();
  const res = await apiFetch(`/api/admin/posts${q ? `?${q}` : ''}`);
  const data = await res.json();
  return data.posts || [];
}

export async function createPost(post: Partial<Post>) {
  return apiFetch('/api/admin/posts', { method: 'POST', body: JSON.stringify(post) });
}

export async function updatePost(id: string, post: Partial<Post>) {
  return apiFetch(`/api/admin/posts/${id}`, { method: 'PUT', body: JSON.stringify(post) });
}

export async function deletePost(id: string) {
  return apiFetch(`/api/admin/posts/${id}`, { method: 'DELETE' });
}

/**
 * Explicit status transitions. Unlike a plain update, these set/clear
 * publish_at correctly — publishing a scheduled post early must not keep its
 * original (future) publish_at, which would keep it hidden.
 */
/**
 * One-by-one cutover: move a single post to the dynamic pipeline (served from
 * D1 by the Pages Function). The Worker bumps updated_at and pings IndexNow so
 * crawlers re-read the URL; nothing is rebuilt.
 */
export async function makePostDynamic(id: string) {
  return apiFetch(`/api/admin/posts/${id}/transition`, {
    method: 'POST',
    body: JSON.stringify({ action: 'dynamic' }),
  });
}

export async function transitionPost(
  id: string,
  action: 'publish' | 'schedule' | 'draft' | 'disable',
  publish_at?: number,
) {
  return apiFetch(`/api/admin/posts/${id}/transition`, {
    method: 'POST',
    body: JSON.stringify({ action, publish_at }),
  });
}

export const ANALYTICS_RANGES: Record<string, number> = {
  '1min': 60, '5min': 300, '10min': 600, '30min': 1800,
  '1h': 3600, '4h': 14400, '8h': 28800, '12h': 43200, '24h': 86400,
  '2d': 172800, '7d': 604800, '15d': 1296000, '1m': 2592000, '3m': 7776000,
  '6m': 15552000, '1y': 31536000, 'all': 315360000,
};

export interface CustomRange { from: number; to: number; }

function customQs(custom?: CustomRange): string {
  if (!custom) return '';
  return `&custom_from=${custom.from}&custom_to=${custom.to}`;
}

export async function fetchAnalyticsCount(type: string, range: string, custom?: CustomRange): Promise<number> {
  const res = await apiFetch(`/api/admin/analytics?type=${type}&range=${range}${customQs(custom)}`);
  const data = await res.json();
  return data.count || 0;
}

export interface SeriesPoint {
  bucketStart: number;
  count: number;
}

export async function fetchAnalyticsSeries(type: string, range: string, buckets = 12, custom?: CustomRange): Promise<SeriesPoint[]> {
  const res = await apiFetch(`/api/admin/analytics/series?type=${type}&range=${range}&buckets=${buckets}${customQs(custom)}`);
  const data = await res.json();
  return data.series || [];
}

export interface BreakdownItem {
  label: string;
  count: number;
}

export type BreakdownMetric =
  | 'posts' | 'providers' | 'engines' | 'countries' | 'browsers'
  | 'devices' | 'referrers' | 'retention' | 'api_paths' | 'api_keys'
  | 'extend_stats' | 'social' | 'blog_referrers' | 'blog_engines';

export async function fetchAnalyticsBreakdown(metric: BreakdownMetric, range: string, custom?: CustomRange): Promise<BreakdownItem[]> {
  const res = await apiFetch(`/api/admin/analytics/breakdown?metric=${metric}&range=${range}${customQs(custom)}`);
  const data = await res.json();
  return data.items || [];
}

export interface LiveBreakdownItem {
  pageType: string;
  lang: string;
  count: number;
}

export interface RealtimeStats {
  activeNow: number;
  eventsLastHour: number;
  emailsToday: number;
  totalVisits: number;
  liveBreakdown: LiveBreakdownItem[];
  /** Real received mail in the last 24h (messages table), not inbox creations. */
  messages24h: number;
}

export async function fetchRealtimeStats(window = '5min'): Promise<RealtimeStats> {
  const res = await apiFetch(`/api/admin/analytics/realtime?window=${window}`);
  const data = await res.json();
  return {
    activeNow: data.activeNow || 0,
    eventsLastHour: data.eventsLastHour || 0,
    emailsToday: data.emailsToday || 0,
    totalVisits: data.totalVisits || 0,
    liveBreakdown: data.liveBreakdown || [],
    messages24h: data.messages24h || 0,
  };
}

export interface UniqueVisitorSeries {
  series: SeriesPoint[];
  totalUnique: number;
}

export async function fetchUniqueVisitors(range: string, buckets = 14, custom?: CustomRange): Promise<UniqueVisitorSeries> {
  const res = await apiFetch(`/api/admin/analytics/unique-visitors?range=${range}&buckets=${buckets}${customQs(custom)}`);
  const data = await res.json();
  return { series: data.series || [], totalUnique: data.totalUnique || 0 };
}

export interface MediaStats {
  total_uploads: number; total_deletes: number; active: number;
  current_files: number; range_uploads: number; range_deletes: number;
}

export async function fetchMediaStats(range = 'all', custom?: CustomRange): Promise<MediaStats> {
  const res = await apiFetch(`/api/admin/media/stats?range=${range}${customQs(custom)}`);
  if (!res.ok) return { total_uploads: 0, total_deletes: 0, active: 0, current_files: 0, range_uploads: 0, range_deletes: 0 };
  return res.json();
}

export interface PostStats {
  count: number;
  sessions: number;
  organic_clicks: number;
  direct: number;
  series: SeriesPoint[];
  breakdown: {
    sources: BreakdownItem[];
    referrers: BreakdownItem[];
    countries: BreakdownItem[];
    browsers: BreakdownItem[];
    devices: BreakdownItem[];
    engines: BreakdownItem[];
  };
}

export async function fetchPostStats(slug: string, range: string, buckets = 14, custom?: CustomRange): Promise<PostStats> {
  const res = await apiFetch(`/api/admin/analytics/post-stats?slug=${encodeURIComponent(slug)}&range=${range}&buckets=${buckets}${customQs(custom)}`, {}, { noRedirectOn401: true });
  if (!res.ok) throw new Error(`fetchPostStats ${res.status}`);
  const data = await res.json();
  return {
    count: data.count || 0,
    sessions: data.sessions || 0,
    organic_clicks: data.organic_clicks || 0,
    direct: data.direct || 0,
    series: data.series || [],
    breakdown: {
      sources: data.breakdown?.sources || [],
      referrers: data.breakdown?.referrers || [],
      countries: data.breakdown?.countries || [],
      browsers: data.breakdown?.browsers || [],
      devices: data.breakdown?.devices || [],
      engines: data.breakdown?.engines || [],
    },
  };
}

export interface BlogStats {
  total: number;
  published: number;
  draft: number;
  impressions: number;
  organic_clicks: number;
}

export async function fetchBlogStats(range = 'all', custom?: CustomRange): Promise<BlogStats> {
  const qs = new URLSearchParams({ range });
  if (custom) { qs.set('custom_from', String(custom.from)); qs.set('custom_to', String(custom.to)); }
  const res = await apiFetch(`/api/admin/blog-stats?${qs}`);
  const data: Record<string, number> = res.ok ? await res.json() : {};
  return {
    total: data.total || 0,
    published: data.published || 0,
    draft: data.draft || 0,
    impressions: data.impressions || 0,
    organic_clicks: data.organic_clicks || 0,
  };
}

// Returns a map of slug → all-time view count for every post that has been visited.
export async function fetchPostViews(): Promise<Record<string, number>> {
  const res = await apiFetch('/api/admin/analytics/post-views');
  if (!res.ok) return {};
  const data = await res.json();
  return data.views || {};
}

// ─── Dashboard bundle ────────────────────────────────────────────────────────
export interface DashboardBundle {
  realtime: RealtimeStats;
  counts: { email: number; visit: number; allVisits: number; session: number; postVisit: number; apiCall: number; };
  uniqueVisitors: number;
  inboxStats: { total: number; active: number; total_messages: number; };
  blogStats: { total: number; published: number; draft: number; };
}

export async function fetchDashboardBundle(window = '5min'): Promise<DashboardBundle> {
  const res = await apiFetch(`/api/admin/analytics/dashboard-bundle?window=${window}`);
  const d = await res.json();
  return {
    realtime:      d.realtime      || { activeNow: 0, eventsLastHour: 0, emailsToday: 0, totalVisits: 0, liveBreakdown: [], messages24h: 0 },
    counts:        d.counts        || { email: 0, visit: 0, allVisits: 0, session: 0, postVisit: 0, apiCall: 0 },
    uniqueVisitors: d.uniqueVisitors ?? 0,
    inboxStats:    d.inboxStats    || { total: 0, active: 0, total_messages: 0 },
    blogStats:     d.blogStats     || { total: 0, published: 0, draft: 0 },
  };
}

export interface AdminStats {
  activeNow: number;
  eventsLastHour: number;
  emailsToday: number;
  totalVisits: number;
  messages24h: number;
}

export async function fetchAdminStats(): Promise<AdminStats> {
  const res = await apiFetch('/api/admin/stats');
  const d = await res.json();
  return {
    activeNow: d.activeNow ?? 0,
    eventsLastHour: d.eventsLastHour ?? 0,
    emailsToday: d.emailsToday ?? 0,
    totalVisits: d.totalVisits ?? 0,
    messages24h: d.messages24h ?? 0,
  };
}

// ─── Analytics full-bundle ───────────────────────────────────────────────────
export interface AnalyticsBundle {
  series: SeriesPoint[];
  uniqueSeries: SeriesPoint[];
  totalUnique: number;
  realtime: RealtimeStats;
  breakdowns: {
    posts: BreakdownItem[]; providers: BreakdownItem[]; engines: BreakdownItem[];
    social: BreakdownItem[]; countries: BreakdownItem[]; browsers: BreakdownItem[];
    devices: BreakdownItem[]; referrers: BreakdownItem[]; retention: BreakdownItem[];
    api_paths: BreakdownItem[]; api_keys: BreakdownItem[]; extend_stats: BreakdownItem[];
  };
  blogStats: BlogStats;
  blogReferrers: BreakdownItem[];
  blogEngines: BreakdownItem[];
}

export async function fetchAnalyticsBundle(
  type: string, range: string, window: string, buckets = 24, custom?: CustomRange,
): Promise<AnalyticsBundle> {
  const qs = new URLSearchParams({ type, range, window, buckets: String(buckets) });
  if (custom) { qs.set('custom_from', String(custom.from)); qs.set('custom_to', String(custom.to)); }
  const res = await apiFetch(`/api/admin/analytics/full-bundle?${qs}`);
  const d = await res.json();
  const empty: BreakdownItem[] = [];
  return {
    series:       d.series       || [],
    uniqueSeries: d.uniqueSeries || [],
    totalUnique:  d.totalUnique  ?? 0,
    realtime:     d.realtime     || { activeNow: 0, eventsLastHour: 0, emailsToday: 0, totalVisits: 0, liveBreakdown: [] },
    breakdowns: {
      posts:        d.breakdowns?.posts        || empty,
      providers:    d.breakdowns?.providers    || empty,
      engines:      d.breakdowns?.engines      || empty,
      social:       d.breakdowns?.social       || empty,
      countries:    d.breakdowns?.countries    || empty,
      browsers:     d.breakdowns?.browsers     || empty,
      devices:      d.breakdowns?.devices      || empty,
      referrers:    d.breakdowns?.referrers    || empty,
      retention:    d.breakdowns?.retention    || empty,
      api_paths:    d.breakdowns?.api_paths    || empty,
      api_keys:     d.breakdowns?.api_keys     || empty,
      extend_stats: d.breakdowns?.extend_stats || empty,
    },
    blogStats:     d.blogStats     || { total: 0, published: 0, draft: 0, impressions: 0, organic_clicks: 0 },
    blogReferrers: d.blogReferrers || empty,
    blogEngines:   d.blogEngines   || empty,
  };
}

export interface SocialPlatformStats {
  platform: string;
  total: number;
  unique: number;
  new_visitors: number;
  returning_visitors: number;
  series: { bucket: number; count: number; unique: number }[];
}

export interface SocialDetailBundle {
  platforms: SocialPlatformStats[];
  bucketSize: number;
  since: number;
  granularity: 'daily' | 'weekly' | 'monthly';
}

export async function fetchSocialDetail(range: string, custom?: CustomRange): Promise<SocialDetailBundle> {
  const qs = new URLSearchParams({ range });
  if (custom) { qs.set('custom_from', String(custom.from)); qs.set('custom_to', String(custom.to)); }
  const empty: SocialDetailBundle = { platforms: [], bucketSize: 86400, since: 0, granularity: 'daily' };
  try {
    const res = await apiFetch(`/api/admin/analytics/social-detail?${qs}`, {}, { noRedirectOn401: true });
    if (!res.ok) return empty;
    const d = await res.json();
    return { platforms: d.platforms || [], bucketSize: d.bucketSize || 86400, since: d.since || 0, granularity: d.granularity || 'daily' };
  } catch { return empty; }
}

export function buildExportUrl(range: string, type?: string, custom?: CustomRange): string {
  const token = localStorage.getItem('admin_token');
  const base = (import.meta.env.VITE_API_BASE as string) || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.')) ? 'http://localhost:3004' : 'https://api.inboxme.space');
  const qs = new URLSearchParams({ range });
  if (type) qs.set('type', type);
  // Custom-range exports used to silently fall back to the default window.
  if (custom) { qs.set('custom_from', String(custom.from)); qs.set('custom_to', String(custom.to)); }
  if (token) qs.set('token', token);
  return `${base}/api/admin/export/analytics?${qs}`;
}

export interface ApiKey {
  id: string;
  key: string;
  label: string;
  status: string;
  /** free | basic | pro — drives the public API creation limits. */
  tier: string;
  rate_limit_rpm: number;
  allowed_ips: string[];
  meta: Record<string, unknown>;
  created_at: number;
  revoked_at: number | null;
  last_used_at: number | null;
  request_count: number;
}

export async function updateApiKey(id: string, data: { label?: string; rate_limit_rpm?: number; allowed_ips?: string[]; meta?: Record<string, unknown>; tier?: string }) {
  return apiFetch(`/api/admin/apikeys/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export interface FeatureFlag {
  key: string;
  enabled: number;
  rollout_pct: number;
  description: string | null;
  updated_at: number;
}

export async function fetchFeatureFlags(): Promise<FeatureFlag[]> {
  const res = await apiFetch('/api/admin/feature-flags');
  const data = await res.json();
  return data.flags || [];
}

export async function updateFeatureFlag(key: string, data: { enabled?: boolean; rollout_pct?: number }) {
  return apiFetch(`/api/admin/feature-flags/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export interface MediaObject {
  key: string;
  size: number;
  uploaded: string | null;
  url: string;
}

export async function fetchMedia(prefix = ''): Promise<MediaObject[]> {
  const res = await apiFetch(`/api/admin/media${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`);
  const data = await res.json();
  return data.objects || [];
}

export async function uploadMedia(file: File, key?: string): Promise<{ url: string; key: string }> {
  const fd = new FormData();
  fd.append('file', file);
  if (key) fd.append('key', key);
  const token = localStorage.getItem('admin_token');
  const base = (import.meta.env.VITE_API_BASE as string) || (window.location.hostname === 'localhost' ? 'http://localhost:3004' : 'https://api.inboxme.space');
  const res = await fetch(`${base}/api/admin/media/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const data = await res.json();
  // Return an absolute URL if the backend returned a relative path
  if (data.url && data.url.startsWith('/')) {
    data.url = `${base}${data.url}`;
  }
  return data;
}

export async function uploadMediaFromUrl(srcUrl: string): Promise<{ url: string; key: string }> {
  const res = await apiFetch('/api/admin/media/upload-url', {
    method: 'POST',
    body: JSON.stringify({ url: srcUrl }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || 'URL upload failed');
  }
  return res.json();
}

export async function deleteMedia(key: string) {
  return apiFetch(`/api/admin/media/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export interface PostRevision {
  id: string;
  post_id: string;
  title: string;
  meta_description: string | null;
  status: string;
  saved_at: number;
  saved_by: string;
}

export async function fetchRevisions(postId: string): Promise<PostRevision[]> {
  const res = await apiFetch(`/api/admin/posts/${postId}/revisions`);
  const data = await res.json();
  return data.revisions || [];
}

export async function restoreRevision(revisionId: string) {
  return apiFetch(`/api/admin/revisions/${revisionId}/restore`, { method: 'POST' });
}

export interface AbuseReport {
  id: string;
  address: string | null;
  message_id: string | null;
  reported_at: number;
  reason: string | null;
  reporter_ip_hash: string | null;
  resolved: number;
  resolved_at: number | null;
  notes: string | null;
}

export async function fetchAbuseReports(): Promise<AbuseReport[]> {
  const res = await apiFetch('/api/admin/abuse-reports');
  const data = await res.json();
  return data.reports || [];
}

export async function updateAbuseReport(id: string, data: { resolved?: boolean; notes?: string }) {
  return apiFetch('/api/admin/abuse-reports', { method: 'PATCH', body: JSON.stringify({ id, ...data }) });
}

export async function fetchApiKeys(): Promise<ApiKey[]> {
  const res = await apiFetch('/api/admin/apikeys');
  const data = await res.json();
  return data.keys || [];
}

export async function createApiKey(label: string): Promise<ApiKey> {
  const res = await apiFetch('/api/admin/apikeys', { method: 'POST', body: JSON.stringify({ label }) });
  const data = await res.json();
  return data.key;
}

export async function revokeApiKey(id: string) {
  return apiFetch(`/api/admin/apikeys/${id}/revoke`, { method: 'POST' });
}

export async function deleteApiKey(id: string) {
  return apiFetch(`/api/admin/apikeys/${id}`, { method: 'DELETE' });
}

export interface AddressActivity {
  id: string;
  address: string;
  provider: string;
  created_at: number;
  expires_at: number | null;
  extend_count: number;
  event_count: number;
}

export async function fetchActivity(limit = 50): Promise<AddressActivity[]> {
  const res = await apiFetch(`/api/admin/activity?limit=${limit}`);
  const data = await res.json();
  return data.addresses || [];
}

// Site settings
export async function fetchSiteSettings(): Promise<Record<string, string>> {
  const res = await apiFetch('/api/admin/site-settings');
  return res.ok ? res.json() : {};
}
export async function saveSiteSettings(data: Record<string, string>) {
  return apiFetch('/api/admin/site-settings', { method: 'PUT', body: JSON.stringify(data) });
}

// Global search
export interface SearchResults {
  posts: { id: string; title: string; slug: string; lang: string; status: string }[];
  inboxes: { address: string; provider: string; created_at: number }[];
  logs: { id: string; action: string; admin_email: string; entity_type: string; ts: number }[];
}
export async function globalSearch(q: string): Promise<SearchResults> {
  const res = await apiFetch(`/api/admin/search?q=${encodeURIComponent(q)}`);
  return res.ok ? res.json() : { posts: [], inboxes: [], logs: [] };
}

// Webhook test
export async function testWebhook(id: string): Promise<{ ok: boolean; status: number; duration_ms: number; error?: string }> {
  const res = await apiFetch(`/api/admin/webhooks/${id}/test`, { method: 'POST' });
  return res.json();
}

// Bulk inbox actions
export async function bulkInboxAction(action: 'delete' | 'expire', addresses: string[]) {
  return apiFetch('/api/admin/inboxes/bulk', { method: 'POST', body: JSON.stringify({ action, addresses }) });
}

// Admin inbox messages
export async function fetchInboxMessages(address: string) {
  const res = await apiFetch(`/api/admin/inboxes/${encodeURIComponent(address)}/messages`);
  return res.ok ? (await res.json()).messages || [] : [];
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await apiFetch('/api/admin/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to change password');
  }
}

export { apiFetch };
